// src/app/chat/fms-web-socket.service.ts
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
// Rxjs
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { Subject, Observable, EMPTY, timer, Subscription } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class FMSWebSocketService {

  /* ---------------- Platform ---------------- */
  private readonly platformId = inject(PLATFORM_ID);

  /* ---------------- Config ---------------- */
  private readonly URL = 'wss://fms.fbt.se/';
  private readonly RECONNECT_INTERVAL_MS = 3000; // only used for optional auto-retry
  private readonly MAX_AUTO_RETRIES = 1;        // keep 0 for manual-only reconnect

  /* ---------------- Internal state ---------------- */
  private socket$: WebSocketSubject<any> | null = null;
  private socketSub: Subscription | null = null;
  private retrySub: Subscription | null = null;
  private reopenTimer: ReturnType<typeof setTimeout> | null = null;

  /** Stable stream for consumers. Never replace this Subject. */
  private readonly frames$ = new Subject<any>();

  /** Guards */
  private opening = false;           // prevent concurrent open attempts
  private manualClose = false;       // true when we intentionally close
  private reopenAfterClose = false;  // request to reopen once the close finishes
  private retries = 0;               // optional auto-retry counter

  /* ============================================================
   * Public API
   * ============================================================ */

  /** Subscribe once; stream remains valid across reconnects. */
  connect(): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    this.ensureSocket();
    return this.frames$.asObservable();
  }

  /** Send a frame if connected. */
  sendMessage(message: any): void {
    if (this.socket$ && !this.socket$.closed) {
      this.socket$.next(message);
    }
  }

  /** Cleanly close; no auto-reconnect afterward. */
  disconnect(): void {
    this.manualClose = true;
    this.reopenAfterClose = false;
    this.retries = 0;
    this.cancelPendingReopen();
    try { 
      this.socket$?.complete(); 
    } catch {}
    this.socketSub?.unsubscribe();
    this.socketSub = null;
    this.socket$ = null;
    // closeObserver will also run and keep guards consistent
  }

  /**
   * Hard reconnect without race conditions:
   * - if socket exists → request reopen after close and complete the socket
   * - if no socket → open immediately
   */
  reconnect(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    // If an open attempt is already in-flight, ignore extra calls
    if (this.opening) return;

    if (this.socket$) {
      if (!this.socket$.closed) {
        this.reopenAfterClose = true;
        this.manualClose = true; // mark intentional close to skip auto-retry
        try { this.socket$.complete(); } catch { }
        // open will happen from closeObserver when close finishes
        return;
      }
      // If it exists but closed, just reopen
      this.socket$ = null;
    }

    this.openSocket();
  }

  /** Optional helper for UI/guards */
  isConnected(): boolean {
    return !!this.socket$ && !this.socket$.closed;
  }

  /* ============================================================
   * Internals
   * ============================================================ */

  private ensureSocket(): void {
    if (this.socket$ || this.opening) return;
    this.openSocket();
  }

  /** Cancel any pending retry timers or hand-off timeouts. */
  private cancelPendingReopen(): void {
    if (this.reopenTimer) {
      clearTimeout(this.reopenTimer);
      this.reopenTimer = null;
    }
    this.retrySub?.unsubscribe();
    this.retrySub = null;
  }

  /** Schedule a clean automated retry. */
  private scheduleRetry(): void {
    this.cancelPendingReopen();
    this.retrySub = timer(this.RECONNECT_INTERVAL_MS).subscribe(() => {
      if (this.manualClose) return;
      this.openSocket();
    });
  }

  /** Create and wire a new WebSocketSubject to the stable frames$. */
  private openSocket(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (this.opening || this.socket$) return; // guard

    this.opening = true;
    this.cancelPendingReopen();

    const socket = webSocket({
      url: this.URL,
      // Be explicit about JSON framing
      serializer: (value: any) => JSON.stringify(value),
      deserializer: (e: MessageEvent<any>) => {
        try { return JSON.parse(e.data); } catch { return e.data; }
      },
      openObserver: {
        next: () => {
          this.retries = 0;
          this.manualClose = false; // back to normal
          this.opening = false;
          // console.debug('[WS] open');
        },
      },
      closeObserver: {
        next: (ev) => {
          // console.warn('[WS] close', ev);
          this.socket$ = null;
          this.opening = false;

          if (this.reopenAfterClose) {
            // Hand-off reopen: only after this close completes
            this.reopenAfterClose = false;
            this.manualClose = false;
            this.reopenTimer = setTimeout(() => this.openSocket(), 50);
            return;
          }

          // Optional auto-retry when NOT manual close
          if (!this.manualClose && this.MAX_AUTO_RETRIES > 0 && this.retries < this.MAX_AUTO_RETRIES) {
            this.retries++;
            this.scheduleRetry();
          }
        },
      },
    });

    this.socketSub?.unsubscribe();
    // Forward frames into the stable Subject
    this.socketSub = socket.subscribe({
      next: (msg) => this.frames$.next(msg),
      error: () => {
        // error will also trigger closeObserver eventually
        this.socket$ = null;
        this.opening = false;

        if (!this.manualClose && this.MAX_AUTO_RETRIES > 0 && this.retries < this.MAX_AUTO_RETRIES) {
          this.retries++;
          this.scheduleRetry();
        }
      },
      complete: () => {
        // Handled via closeObserver
      },
    });

    this.socket$ = socket;
  }
}